import React from "react"
import { FormProvider } from "react-hook-form"
import { useTranslation } from "react-i18next"

import { ApiResponse } from "shared/types/ApiResponse"
import { FilePreview } from "shared/types/File"
import { MAX_LENGTH_PROJECT_NAME } from "shared/types/Project"
import { FormProviderProps } from "shared/types/ReactHookForm"
import { SelectOption } from "shared/types/Select"
import {
  AttachmentType,
  ProjectImportError
} from "shared/types/__generated__/graphql"
import { Select } from "shared/ui/input/select/select/Select"
import { TextInput } from "shared/ui/input/text/TextInput"
import { FormOverlay } from "shared/ui/overlay/FormOverlay"

import {
  ProjectFormData,
  ProjectFormState
} from "features/project-management/types/ProjectFormData"

import "./styles/_new-project-overlay.scss"
import { ExampleXlsxLink } from "./ui/ExampleXlsxLink"
import { XlsxFileUpload } from "./ui/XlsxFileUpload"
import { XlsxImportErrorHint } from "./ui/XlsxImportErrorHint"
import { XlsxUploadErrorHint } from "./ui/XlsxUploadErrorHint"

export interface NewProjectOverlayViewModel {
  autoFocus?: boolean
  form: FormProviderProps<ProjectFormState, unknown, ProjectFormData>
  projectPhaseOptions: SelectOption<string>[]
  isLoadingProjectPhaseOptions?: boolean
  isXlsxUploadDisabled?: boolean
  isSubmitting?: boolean
  onSubmit: () => void
  onCancel: () => void
  onXlsxUpload?: (file: FilePreview) => Promise<ApiResponse<unknown>>
  onXlsxUploadComplete: (
    successes: FilePreview[],
    failures: FilePreview[]
  ) => void
  onXlsxDelete: () => void
  xlsxFile?: AttachmentType
  xlsxExampleUrl?: string
  xlsxUploadError?: string
  xlsxUploadErrorHintId: string
  xlsxImportErrors?: ProjectImportError[]
  xlsxImportErrorHintId: string
}

export const NewProjectOverlayView = React.memo(
  ({
    autoFocus = true,
    form,
    projectPhaseOptions,
    isLoadingProjectPhaseOptions,
    isXlsxUploadDisabled,
    isSubmitting,
    onSubmit,
    onCancel,
    onXlsxUpload,
    onXlsxUploadComplete,
    onXlsxDelete,
    xlsxFile,
    xlsxExampleUrl,
    xlsxUploadError,
    xlsxUploadErrorHintId,
    xlsxImportErrors,
    xlsxImportErrorHintId
  }: NewProjectOverlayViewModel) => {
    const { t } = useTranslation()

    const activeHintIds =
      [
        xlsxUploadError ? xlsxUploadErrorHintId : undefined,
        xlsxImportErrors ? xlsxImportErrorHintId : undefined
      ]
        .filter(Boolean)
        .join(" ") || undefined

    return (
      <FormOverlay
        id="new-project-overlay"
        form="new-project-overlay-form"
        className="form-overlay--small"
        headline={t("Project.Actions.Create")}
        submitButtonText={t("NewProjectOverlay.SubmitButton")}
        prefix="new-project-overlay-form"
        isLoading={isSubmitting}
        isSubmitDisabled={isSubmitting}
        onCancel={onCancel}
        showCloseButton
      >
        <FormProvider {...form}>
          <form
            id="new-project-overlay-form"
            className="overlay-form"
            onSubmit={onSubmit}
          >
            <p className="new-project-overlay__subline">
              {t("NewProjectOverlay.Subline")}
            </p>
            <TextInput.Root
              name="project.title"
              label={t("Project.title")}
              autoFocus={autoFocus}
              required
              maxLength={MAX_LENGTH_PROJECT_NAME}
            >
              <TextInput.Label />
              <TextInput.Focus>
                <TextInput.Input
                  type="text"
                  placeholder={t("NewProjectOverlay.ProjectName.Placeholder")}
                  data-testid="project-title-input"
                />
                <TextInput.Counter />
              </TextInput.Focus>
              <TextInput.Readonly />
              <TextInput.ErrorMessage />
            </TextInput.Root>
            <Select.Root
              name="project.phase"
              label={t("Project.phase")}
              required
              loading={isLoadingProjectPhaseOptions}
            >
              <Select.Label />
              <Select.Input
                options={projectPhaseOptions}
                valueIsOption
                placeholder={t("NewProjectOverlay.ProjectPhase.Placeholder")}
              />
              <Select.ErrorMessage />
            </Select.Root>
            <div className="new-project-overlay__xlsx-upload">
              <XlsxFileUpload
                xlsxFile={xlsxFile}
                onXlsxUpload={onXlsxUpload}
                onXlsxUploadComplete={onXlsxUploadComplete}
                onXlsxDelete={onXlsxDelete}
                isDisabled={isXlsxUploadDisabled}
                hintId={activeHintIds}
              />
              <XlsxImportErrorHint
                id={xlsxImportErrorHintId}
                errors={xlsxImportErrors}
              />
              <XlsxUploadErrorHint
                id={xlsxUploadErrorHintId}
                error={xlsxUploadError}
              />
              <ExampleXlsxLink url={xlsxExampleUrl} />
            </div>
          </form>
        </FormProvider>
      </FormOverlay>
    )
  }
)
